import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';

import { TOAST_DATA, TOAST_REF, ToastData, ToastRef } from './toast.service';

@Component({
    selector: 'app-toast',
    templateUrl: './toast.component.html',
    styleUrls: ['toast.component.scss'],
    animations: [
        trigger('slideIn', [
            state('in', style({ transform: 'translateX(0%)', opacity: 1 })),
            transition('void => *', [
                style({ transform: 'translateX(100%)', opacity: 0 }),
                animate('300ms ease-in')
            ]),
            transition('* => void', [
                animate('300ms ease-out', style({ transform: 'translateX(100%)', opacity: 0 }))
            ])
        ])
    ],
    imports: [NgClass],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class ToastComponent implements OnInit, OnDestroy {
    readonly data = inject<ToastData>(TOAST_DATA);
    readonly ref = inject<ToastRef>(TOAST_REF);

    private timeoutId: any;
    public visible: boolean = true;

    ngOnInit() {
        const duration = this.data.duration ?? this.getDefaultDuration();
        
        this.timeoutId = setTimeout(() => {
            this.close();
        }, duration);
    }

    ngOnDestroy() {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
    }

    close() {
        this.visible = false;
        // Small delay to allow animation to complete
        setTimeout(() => this.ref.close(), 300);
    }

    private getDefaultDuration(): number {
        switch (this.data.type) {
            case 'error':
                return 5000;
            case 'warning':
                return 4000;
            case 'debug':
                return 8000;
            default:
                return 3000;
        }
    }

    getToastClasses(): string {
        const baseClasses = 'toast-container';
        
        switch (this.data.type) {
            case 'success':
                return `${baseClasses} toast-success`;
            case 'error':
                return `${baseClasses} toast-error`;
            case 'warning':
                return `${baseClasses} toast-warning`;
            case 'info':
                return `${baseClasses} toast-info`;
            case 'debug':
                return `${baseClasses} toast-debug`;
            default:
                return `${baseClasses} toast-success`;
        }
    }

    getIconPath(): string {
        switch (this.data.type) {
            case 'success':
                return 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
            case 'error':
                return 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z';
            case 'warning':
                return 'M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z';
            case 'info':
                return 'M11.25 11.25l.041-.20a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
            case 'debug':
                return 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655-4.653a2.548 2.548 0 010-3.586l.823-.823a2.548 2.548 0 013.586 0l4.655 4.653M3.75 12l8.003-8.003a1.125 1.125 0 011.591 0l2.659 2.659a1.125 1.125 0 010 1.591L7.5 16.5';
            default:
                return 'M11.25 11.25l.041-.020a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z';
        }
    }

    getProgressDuration(): number {
        return this.data.duration ?? this.getDefaultDuration();
    }
}